import { useEffect, useRef } from 'react';
import { setCurrentSession, clearCurrentSession } from '../../store/cashSessionSlice';
import { setCurrentOrder } from '../../store/orderSlice';

export default function useDashboardSession({
  ACTIVE_DRAFT_ORDER_ID_KEY,
  checkMe,
  currentOrderIdRef,
  cartRef,
  currentSession,
  currentSessionRef,
  dispatch,
  hasRestoredDraftRef,
  isSessionInitializing,
  setCart,
  setIsOpenSessionModalOpen,
  setIsSessionInitializing,
  setOrderNotes,
  setSelectedProduct,
  showToast,
  terminalId,
  triggerGetOrder,
  triggerGetSession,
  triggerListSessions,
}) {
  const hasSessionBeenOpen = useRef(false);

  useEffect(() => {
    const poll = setInterval(async () => {
      checkMe();
      const sessionId = currentSessionRef.current?.id;
      if (sessionId) {
        try {
          const res = await triggerGetSession(sessionId).unwrap();
          const liveSession = res?.data?.session;
          if (liveSession && Number(liveSession.pos_terminal_id) === Number(terminalId)) {
            dispatch(setCurrentSession(liveSession));
          }
        } catch (_) {}
      }
    }, 30000);
    return () => clearInterval(poll);
  }, [checkMe, currentSessionRef, dispatch, terminalId, triggerGetSession]);

  useEffect(() => {
    const initSession = async () => {
      if (!terminalId) {
        setIsSessionInitializing(false);
        return;
      }

      try {
        const stored = localStorage.getItem('cashSession');
        let shouldResolveFromList = true;

        if (stored) {
          let parsed = null;
          try {
            parsed = JSON.parse(stored);
          } catch {
            dispatch(clearCurrentSession());
          }

          if (parsed?.id) {
            const cachedTerminalId = Number(parsed.pos_terminal_id);
            if (cachedTerminalId && cachedTerminalId !== Number(terminalId)) {
              dispatch(clearCurrentSession());
            } else {
              dispatch(setCurrentSession(parsed));
              try {
                const res = await triggerGetSession(parsed.id).unwrap();
                const liveSession = res?.data?.session;
                if (liveSession && Number(liveSession.pos_terminal_id) === Number(terminalId)) {
                  dispatch(setCurrentSession(liveSession));
                  shouldResolveFromList = false;
                } else {
                  dispatch(clearCurrentSession());
                }
              } catch {
                dispatch(clearCurrentSession());
              }
            }
          }
        }

        if (shouldResolveFromList) {
          try {
            const res = await triggerListSessions({ per_page: 50 }).unwrap();
            const sessions = res?.data?.sessions || [];
            const terminalSessions = sessions.filter(
              (session) => Number(session.pos_terminal_id) === Number(terminalId)
            );
            const openSession = terminalSessions.find((session) => session.status === 'open');
            const closedSession = terminalSessions.find((session) => session.status === 'closed');
            if (openSession) {
              try {
                const detail = await triggerGetSession(openSession.id).unwrap();
                dispatch(setCurrentSession(detail.data.session));
              } catch {
                dispatch(setCurrentSession(openSession));
              }
            } else if (closedSession) {
              dispatch(setCurrentSession(closedSession));
            }
          } catch {}
        }
      } finally {
        setIsSessionInitializing(false);
      }
    };

    initSession();
  }, [dispatch, setIsSessionInitializing, terminalId, triggerGetSession, triggerListSessions]);

  useEffect(() => {
    if (isSessionInitializing) return;

    if (currentSession?.status === 'open') {
      hasSessionBeenOpen.current = true;
      setIsOpenSessionModalOpen(false);
    } else if (currentSession === null) {
      setIsOpenSessionModalOpen(true);
    } else if (currentSession?.status === 'closed' && !hasSessionBeenOpen.current) {
      setIsOpenSessionModalOpen(true);
    } else {
      setIsOpenSessionModalOpen(false);
    }
  }, [currentSession, isSessionInitializing, setIsOpenSessionModalOpen]);

  useEffect(() => {
    if (hasRestoredDraftRef.current) return;
    if (currentSession?.status !== 'open') return;

    const hasLocalDraft = Boolean(currentOrderIdRef.current) || cartRef.current.length > 0;
    if (hasLocalDraft) {
      hasRestoredDraftRef.current = true;
      return;
    }

    const restoreDraft = async () => {
      const draftOrderId = sessionStorage.getItem(ACTIVE_DRAFT_ORDER_ID_KEY);
      if (!draftOrderId) {
        hasRestoredDraftRef.current = true;
        return;
      }

      try {
        const detailRes = await triggerGetOrder(draftOrderId).unwrap();
        const fullOrder = detailRes?.data?.order || detailRes?.data;
        if (!fullOrder?.id || fullOrder.status !== 'draft') {
          sessionStorage.removeItem(ACTIVE_DRAFT_ORDER_ID_KEY);
          return;
        }
        const items = fullOrder.order_items || fullOrder.items || [];
        const restoredCart = items.map((item) => ({
          id: item.product_id,
          name: item.product_name,
          sku: item.product_sku,
          retail_price: item.unit_price,
          qty: parseFloat(item.quantity),
          stock: {},
        }));

        dispatch(setCurrentOrder(fullOrder));
        setCart(restoredCart);
        setSelectedProduct(null);
        setOrderNotes(fullOrder.notes || '');
        if (restoredCart.length > 0) {
          showToast('Draft bill restored', 'info');
        }
      } catch (_) {
        sessionStorage.removeItem(ACTIVE_DRAFT_ORDER_ID_KEY);
      } finally {
        hasRestoredDraftRef.current = true;
      }
    };

    restoreDraft();
  }, [
    ACTIVE_DRAFT_ORDER_ID_KEY,
    cartRef,
    currentOrderIdRef,
    currentSession?.status,
    dispatch,
    hasRestoredDraftRef,
    setCart,
    setOrderNotes,
    setSelectedProduct,
    showToast,
    triggerGetOrder,
  ]);
}
